import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const News = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All' },
    { id: 'founders', name: 'Portfolio Founders' }
  ];

  const founderInsights = [
    {
      id: 'f1',
      title: 'Elon Musk on Tesla\'s Full Self-Driving Progress and Robotaxi Future',
      excerpt: 'Tesla CEO discusses the latest advancements in autonomous driving technology and the company\'s vision for a fully autonomous transportation network.',
      image: 'https://img.youtube.com/vi/GwfLkEOW37Q/maxresdefault.jpg',
      category: 'founders',
      date: 'January 18, 2025',
      tags: ['Tesla', 'Autonomous Driving', 'Innovation'],
      type: 'video',
      videoUrl: 'https://www.youtube.com/watch?v=GwfLkEOW37Q'
    },
    {
      id: 'f2',
      title: 'Jensen Huang: The AI Revolution and NVIDIA\'s Role in Shaping the Future',
      excerpt: 'NVIDIA\'s CEO shares insights on the current AI boom, the importance of accelerated computing, and how artificial intelligence will transform every industry.',
      image: 'https://img.youtube.com/vi/pE6sw_E9Gh0/maxresdefault.jpg',
      category: 'founders',
      date: 'January 16, 2025',
      tags: ['NVIDIA', 'AI', 'Technology'],
      type: 'video',
      videoUrl: 'https://www.youtube.com/watch?v=pE6sw_E9Gh0&t=1478s'
    },
    {
      id: 'f3',
      title: 'All things AI w @altcap @sama & @satyanadella. A Halloween Special. 🎃🔥BG2 w/ Brad Gerstner',
      excerpt: 'A special Halloween episode featuring discussions about AI with industry leaders including Sam Altman and Satya Nadella, hosted by Brad Gerstner.',
      image: 'https://img.youtube.com/vi/Gnl833wXRz0/maxresdefault.jpg',
      category: 'founders',
      date: 'January 14, 2025',
      tags: ['AI', 'Tech Leaders', 'Discussion'],
      type: 'video',
      videoUrl: 'https://www.youtube.com/watch?v=Gnl833wXRz0'
    },
    {
      id: 'f4',
      title: 'Sam Altman on OpenAI\'s Future and the Path to AGI',
      excerpt: 'OpenAI CEO Sam Altman discusses the company\'s roadmap toward artificial general intelligence, the challenges ahead, and the transformative potential of AI technology.',
      image: 'https://img.youtube.com/vi/O4wBUysNe2k/maxresdefault.jpg',
      category: 'founders',
      date: 'January 12, 2025',
      tags: ['OpenAI', 'AGI', 'AI Future'],
      type: 'video',
      videoUrl: 'https://www.youtube.com/watch?v=O4wBUysNe2k&t=51s'
    }
  ];

  const allInsights = selectedCategory === 'founders' 
    ? founderInsights 
    : founderInsights;

  const featuredInsight = allInsights[0];

  const handleVideoClick = (videoUrl?: string) => {
    if (videoUrl) {
      window.open(videoUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
              <a href="/" className="hover:text-gray-700">Palo Alto Innovate</a>
              <span>/</span>
              <span className="text-gray-900 font-medium">News</span>
            </nav>
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 leading-tight">
              Latest News
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl">
              Stay updated with the latest developments from our portfolio companies, featuring exclusive interviews and insights from visionary founders shaping the future of technology and innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 border-b border-gray-200 bg-white sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredInsight && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div 
                className="aspect-[4/3] overflow-hidden rounded-lg relative cursor-pointer"
                onClick={() => handleVideoClick(featuredInsight.videoUrl)}
              >
                <img
                  src={featuredInsight.image}
                  alt={featuredInsight.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                {featuredInsight.type === 'video' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-black/70 rounded-full flex items-center justify-center hover:bg-black/80 transition-colors cursor-pointer">
                      <i className="ri-play-fill text-white text-2xl ml-1"></i>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-4">
                  {featuredInsight.type === 'video' && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                      <i className="ri-video-line mr-1"></i>Video
                    </span>
                  )}
                  {featuredInsight.tags.map((tag, index) => (
                    <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                <h2 
                  className="text-3xl md:text-4xl font-light text-gray-900 mb-4 leading-tight hover:text-gray-700 transition-colors cursor-pointer"
                  onClick={() => handleVideoClick(featuredInsight.videoUrl)}
                >
                  {featuredInsight.title}
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {featuredInsight.excerpt}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Portfolio Founders Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
              Portfolio Founders Updates
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Exclusive interviews and thought leadership from the visionary founders of our portfolio companies.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {founderInsights.slice(1).map((insight) => (
              <article
                key={insight.id}
                className="group bg-white rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
                onClick={() => handleVideoClick(insight.videoUrl)}
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={insight.image}
                    alt={insight.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {insight.type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 bg-black/70 rounded-full flex items-center justify-center group-hover:bg-black/80 transition-colors">
                        <i className="ri-play-fill text-white text-xl ml-0.5"></i>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    {insight.tags.slice(0, 2).map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-3 line-clamp-2 group-hover:text-gray-700 transition-colors leading-tight">
                    {insight.title}
                  </h3>
                  <p className="text-gray-600 line-clamp-3 mb-4 text-sm leading-relaxed">
                    {insight.excerpt}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
            Stay Informed
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Get the latest news, updates, and exclusive content from our portfolio companies delivered to your inbox.
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
            <input
              type="email"
              placeholder="Enter your email address"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              required
            />
            <button
              type="submit"
              className="px-8 py-3 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Subscribe
            </button>
          </form>
          <p className="text-sm text-gray-500 mt-4">
            Join 10,000+ investors and entrepreneurs who stay updated with our news.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default News;
